import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectionService } from '../../service/connection.service';
import { EventService } from '../../service/event.service';
import { SubscriptionService } from '../../service/subscription.service';
import { EventsMapComponent } from '../matrimoni/events-map.component';
import { IconComponent } from '../../components/icon/icon.component';
import { resolveDepositEuro } from '../../utility/deposit';

interface MonthBar {
  label: string;
  count: number;
  amount: number;
  collected: number;
}

import { isQuote } from '../../utility/quote';
@Component({
  selector: 'app-statistiche',
  imports: [CommonModule, RouterLink, EventsMapComponent, TranslatePipe, IconComponent],
  templateUrl: './statistiche.component.html',
  styleUrl: './statistiche.component.css',
})
export class StatisticheComponent implements OnInit {
  private connection = inject(ConnectionService);
  private events = inject(EventService);
  private subscription = inject(SubscriptionService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  loading = true;
  currentYear = new Date().getFullYear();
  selectedYear = this.currentYear;
  years: number[] = [this.currentYear];

  // Base
  totalEvents = 0;
  eventsThisYear = 0;

  // Pro — trasferte
  kmYear = 0;
  kmUpcoming = 0;
  travelCostYear = 0;

  // Pro — città
  cities: { city: string; count: number }[] = [];
  otherCitiesCount = 0;

  // Pro — mese / stagionalità
  months: MonthBar[] = [];
  busiestMonth: MonthBar | null = null;
  maxMonthCount = 0;
  maxMonthAmount = 0;
  maxMonthCollected = 0;

  // Pro — contratti
  signedYear = 0;
  signRate = 0;
  avgEventValue = 0;

  // Pro — fatturato (stima) e incassato reale (da paid_at, introdotto in 3.2.0)
  revenueYear = 0;
  collectedYear = 0;
  paidWithoutDate = 0;

  // Pro — mappa
  yearEventsList: any[] = [];

  private data: any[] = [];

  get canPro(): boolean {
    return this.subscription.canPro();
  }

  ngOnInit(): void {
    this.subscription.loadEntitlements();
    this.connection.get(
      'events',
      (data: any[]) => {
        // Solo eventi confermati: i preventivi non sono entrate
        this.data = (data || []).filter((m) => !isQuote(m));
        const found = this.data.map((m) => this.eventYear(m)).filter((y) => !isNaN(y));
        this.years = Array.from(new Set([this.currentYear, ...found])).sort((a, b) => b - a);
        this.compute();
        this.loading = false;
      },
      () => {
        this.loading = false;
      },
    );
  }

  selectYear(year: number): void {
    this.selectedYear = year;
    this.compute();
  }

  goToPlans(): void {
    this.router.navigate(['/abbonamenti']);
  }

  private eventYear(m: any): number {
    return new Date(m.event?.date).getFullYear();
  }

  private monthLabels(): string[] {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'it';
    return Array.from({ length: 12 }, (_, i) => {
      const label = new Date(2024, i, 1).toLocaleDateString(lang, { month: 'short' }).replace('.', '');
      return label.charAt(0).toUpperCase() + label.slice(1);
    });
  }

  private compute(): void {
    const data = this.data;
    const year = this.selectedYear;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.totalEvents = data.length;
    const yearEvents = data.filter((m) => this.eventYear(m) === year);
    this.yearEventsList = yearEvents;
    this.eventsThisYear = yearEvents.length;

    // Trasferte
    const km = (m: any) => +m?.financial?.travel_fee?.distance_km || 0;
    const cost = (m: any) => +m?.financial?.travel_fee?.cost || 0;
    this.kmYear = Math.round(yearEvents.reduce((s, m) => s + km(m), 0));
    this.kmUpcoming = Math.round(
      data
        .filter((m) => new Date(m.event?.date) >= today)
        .reduce((s, m) => s + km(m), 0),
    );
    this.travelCostYear = yearEvents.reduce((s, m) => s + cost(m), 0);

    // Città
    const cityMap = new Map<string, number>();
    for (const m of yearEvents) {
      const city =
        m.event?.location_place?.city?.trim() || this.extractCity(m.event?.location);
      if (!city) continue;
      cityMap.set(city, (cityMap.get(city) ?? 0) + 1);
    }
    const rankedCities = Array.from(cityMap.entries())
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count);
    this.cities = rankedCities.slice(0, 6);
    this.otherCitiesCount = Math.max(0, rankedCities.length - 6);

    // Eventi + fatturato per mese (mese dell'evento)
    const buckets: MonthBar[] = this.monthLabels().map((label) => ({ label, count: 0, amount: 0, collected: 0 }));
    for (const m of yearEvents) {
      const idx = new Date(m.event?.date).getMonth();
      if (idx < 0 || idx > 11) continue;
      buckets[idx].count++;
      buckets[idx].amount += this.events.computeTotal(m);
    }

    // Incassato reale per mese (mese dell'incasso, su tutti gli eventi)
    this.paidWithoutDate = 0;
    for (const m of data) {
      const total = this.events.computeTotal(m);
      const depositEuro = resolveDepositEuro(m.financial?.deposit, total);
      const parts = [
        { part: m.financial?.deposit, amount: depositEuro },
        { part: m.financial?.balance, amount: Math.max(0, total - depositEuro) },
      ];
      for (const { part, amount } of parts) {
        if (!part?.paid || amount <= 0) continue;
        if (!part.paid_at) {
          if (this.eventYear(m) === year) this.paidWithoutDate += amount;
          continue;
        }
        const d = new Date(part.paid_at);
        if (d.getFullYear() === year) buckets[d.getMonth()].collected += amount;
      }
    }

    this.months = buckets;
    this.maxMonthCount = Math.max(1, ...buckets.map((b) => b.count));
    this.maxMonthAmount = Math.max(1, ...buckets.map((b) => b.amount));
    this.maxMonthCollected = Math.max(1, ...buckets.map((b) => b.collected));
    this.collectedYear = buckets.reduce((s, b) => s + b.collected, 0);
    this.busiestMonth = buckets.reduce(
      (best, b) => (b.count > (best?.count ?? 0) ? b : best),
      null as MonthBar | null,
    );

    // Contratti
    this.signedYear = yearEvents.filter(
      (m) => m.signature_request?.signed_contract_file,
    ).length;
    this.signRate = this.eventsThisYear
      ? Math.round((this.signedYear / this.eventsThisYear) * 100)
      : 0;

    // Valore medio + fatturato stima
    const totals = yearEvents.map((m) => this.events.computeTotal(m));
    this.revenueYear = totals.reduce((s, v) => s + v, 0);
    this.avgEventValue = this.eventsThisYear
      ? Math.round(this.revenueYear / this.eventsThisYear)
      : 0;
  }

  private extractCity(location?: string): string {
    if (!location) return '';
    const parts = location.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return parts[0] ?? '';
    const candidate = parts[parts.length - 2] ?? '';
    return candidate.replace(/^\d{4,5}\s*/, '').replace(/\s+[A-Z]{2}$/, '').trim();
  }

  barHeight(count: number): number {
    return Math.round((count / this.maxMonthCount) * 100);
  }

  revenueBarHeight(amount: number): number {
    return Math.round((amount / this.maxMonthAmount) * 100);
  }

  collectedBarHeight(amount: number): number {
    return Math.round((amount / this.maxMonthCollected) * 100);
  }
}
